import { appendBlocks, type ChatBlock } from "./chatBlocks";
import type { ChatError, PendingKind, StreamOutcome } from "./chatStream";

export type ChatRole = "user" | "assistant";

export type ChatRequest = {
  url: string;
  body: Record<string, unknown>;
};

export type ChatItemStatus = { kind: "stopped" } | { kind: "failed"; error: ChatError };

export type ReplyProgress = { pendingKind: PendingKind | null; statusLabel: string | null };

export type ChatItem = {
  id: number;
  role: ChatRole;
  blocks: ChatBlock[];
  createdAt?: string;
  status?: ChatItemStatus;
  retryRequest?: ChatRequest;
  progress?: ReplyProgress;
  steps?: string[];
};

export const addStep = (steps: string[] | undefined, label: string | null): string[] | undefined => {
  if (!label) return steps;
  const current = steps ?? [];
  return current[current.length - 1] === label ? current : [...current, label];
};

export const applyOutcome = (msg: ChatItem, outcome: StreamOutcome): ChatItem => {
  const settled = { ...msg, progress: undefined };
  switch (outcome.status) {
    case "completed":
      return { ...settled, status: undefined, createdAt: outcome.createdAt ?? msg.createdAt };
    case "stopped":
      return { ...settled, status: { kind: "stopped" } };
    case "failed":
      return { ...settled, status: { kind: "failed", error: outcome.error } };
  }
};

export const announcementFor = (outcome: StreamOutcome): string => {
  switch (outcome.status) {
    case "completed":
      return "Vecta replied";
    case "stopped":
      return "Reply stopped";
    case "failed":
      return "Reply failed";
  }
};

export const updateMessage = (
  currentMessages: ChatItem[],
  messageId: number,
  update: (message: ChatItem) => ChatItem
): ChatItem[] => currentMessages.map((msg) => (msg.id === messageId ? update(msg) : msg));

export const updateMessageBlocks = (
  currentMessages: ChatItem[],
  messageId: number,
  incomingBlocks: ChatBlock[]
): ChatItem[] =>
  updateMessage(currentMessages, messageId, (msg) => ({
    ...msg,
    blocks: appendBlocks(msg.blocks, incomingBlocks),
  }));
